import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useNotification } from '../../../../contexts/NotificationContext';
import { useAuth } from '../../../../contexts/AuthContext';
import apiService from '../../../../services/api';
import OverviewTab from './OverviewTab/index';
import UserManagementTab from './UserManagementTab/index';
import TaskManagementTab from './TaskManagementTab/index';
import LeaderboardTab from './LeaderboardTab';
import PointConfigurationTab from './PointConfigurationTab';
import SettingsTab from './SettingsTab/index';
import styles from '../../../../styles/GroupDetails.module.css';
import tabStyles from '../../../../styles/TabContainer.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';

interface GroupDetailsData {
  id: string;
  name: string;
  description: string;
  category: {
    id: string;
    name: string;
    color: string;
  };
  team_lead: {
    id: string;
    full_name: string;
    email: string;
  };
  access_level: string;
  is_active: boolean;
  created_at: string;
  member_count: number;
  theme_color: string;
  logo: string | null;
  banner: string | null;
}


interface Tab {
  id: string;
  label: string;
  icon: string;
  component: React.ComponentType<any>;
  requiredPermissions?: string[];
  alwaysVisible?: boolean;
}

const GroupDetails: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const notificationService = useNotification();
  const { user } = useAuth();

  const [groupDetails, setGroupDetails] = useState<GroupDetailsData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Always use admin context (consolidated routing)
  const isAdminContext = true;

  // Define all possible tabs with their permission requirements
  const allTabs: Tab[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: '<svg class="tabIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>',
      component: OverviewTab,
      alwaysVisible: true // Overview is always visible to all users
    },
    {
      id: 'points-config',
      label: 'Points Scheme',
      icon: '<svg class="tabIcon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>',
      component: PointConfigurationTab,
      requiredPermissions: ['create_point_config', 'edit_point_config', 'delete_point_config']
    },
    {
      id: 'task-management',
      label: 'Task Management',
      icon: '<svg class="tabIcon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"></path><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>',
      component: TaskManagementTab,
      alwaysVisible: true // Task Management is always visible, actions are controlled by individual permissions
    },
    {
      id: 'user-management',
      label: 'User Management',
      icon: '<svg class="tabIcon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>',
      component: UserManagementTab,
      requiredPermissions: ['add_user', 'edit_user', 'remove_user', 'invitation_management']
    },
    {
      id: 'leader-board',
      label: 'Leader Board',
      icon: '<svg class="tabIcon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M0 0h1v15h15v1H0zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61 4.15-5.073a.5.5 0 0 1 .704-.07"></path></svg>',
      component: LeaderboardTab,
      requiredPermissions: ['view_leaderboard']
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: '<svg class="tab-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>',
      component: SettingsTab,
      requiredPermissions: ['change_settings']
    },
  ];

  // Check if user is system admin
  const isSystemAdmin = user?.role === 'admin';

  // Filter tabs based on group admin status and system admin
  const getVisibleTabs = useCallback((): Tab[] => {
    return allTabs.filter(tab => {
      if (tab.id === 'settings') {
        return isSystemAdmin;
      }

      if (isAdminContext) {
        return true;
      }

      if (tab.alwaysVisible) {
        return true;
      }

      if (tab.requiredPermissions && tab.requiredPermissions.length > 0) {
        return true;
      }

      return true;
    });
  }, [isSystemAdmin]);

  const tabs = getVisibleTabs();

  useEffect(() => {
    if (groupId) {
      loadGroupDetails();
    }
  }, [groupId]);

  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Map URL segments to tab IDs
    const tabMapping: { [key: string]: string } = {
      'overview': 'overview',
      'points-configuration': 'points-config',
      'task-management': 'task-management',
      'user-management': 'user-management',
      'leader-board': 'leader-board',
      'settings': 'settings'
    };

    if (lastSegment && tabMapping[lastSegment]) {
      if (tabMapping[lastSegment] === 'settings' && !isSystemAdmin) {
        navigate(`/group-management/groups/${groupId}/overview`, { replace: true });
        return;
      }
      setActiveTab(tabMapping[lastSegment]);
    } else if (lastSegment === groupId) {
      navigate(`/group-management/groups/${groupId}/overview`, { replace: true });
    }
  }, [location.pathname, groupId, navigate, isSystemAdmin, isAdminContext]);

  const loadGroupDetails = async () => {
    if (!groupId) return;

    try {
      const response = await apiService.getGroupById(groupId);

      if (response.status === 'success') {
        setGroupDetails(response.data);
      } else {
        notificationService.error(response.message || 'Failed to load group details');
        navigate('/group-management/groups');
      }
    } catch (error: any) {
      console.error('Error loading group details:', error);
      notificationService.error(error.response?.data?.message || 'Failed to load group details. Please try again.');
      navigate('/group-management/groups');
    }
  };


  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);

    // Map tab IDs to URL segments
    const urlMapping: { [key: string]: string } = {
      'overview': 'overview',
      'points-config': 'points-configuration',
      'task-management': 'task-management',
      'user-management': 'user-management',
      'leader-board': 'leader-board',
      'settings': 'settings'
    };

    const urlSegment = urlMapping[tabId] || 'overview';
    navigate(`/group-management/groups/${groupId}/${urlSegment}`);
  };

  const hasPermission = (permission: string): boolean => {
    return true; // Admin context always has all permissions
  };

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  if (!groupDetails) {
    return null;
  }

  return (
    <div className={styles.groupDetailsContainer}>
      {/* Header */}
      <div className={styles.groupHeader}>
        <nav className={styles.breadcrumbNav}>
          <div className={styles.navPath}>
            <button
              className={styles.navLink}
              onClick={() => navigate('/group-management/groups')}
            >
              Groups
            </button>
            <span className={styles.navSeparator}>/</span>
            <button
              className={styles.navLink}
              onClick={() => navigate(`/group-management/groups/${groupId}/overview`)}
            >
              {groupDetails.name}
            </button>
            {activeTab !== 'overview' && (
              <>
                <span className={styles.navSeparator}>/</span>
                <span className={styles.navCurrent}>
                  {tabs.find(tab => tab.id === activeTab)?.label}
                </span>
              </>
            )}
          </div>
        </nav>
        <div className={styles.groupActions}>
          <button
            className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
            onClick={() => notificationService.success('Analytics functionality will be implemented')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M4 11H2v3h2zm5-4H7v7h2zm5-5v12h-2V2zm-2-1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM6 7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1zm-5 4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1z"></path>
            </svg>
            Analytics
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={tabStyles.tabsContainer}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`${tabStyles.tab} ${activeTab === tab.id ? tabStyles.active : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <span className={tabStyles.tabIcon} dangerouslySetInnerHTML={{ __html: tab.icon }}></span>
            {tab.label}
          </div>
        ))}
      </div>

      {/* Tab Content */}
      <div className={tabStyles.tabContentContainer}>
        <div className={`${tabStyles.tabContent} ${activeTab === 'overview' ? tabStyles.active : ''}`}>
          {activeTab === 'overview' && ActiveComponent && (
            <ActiveComponent
              groupDetails={groupDetails}
              onGroupUpdate={loadGroupDetails}
              hasPermission={hasPermission}
              isAdminContext={isAdminContext}
            />
          )}
        </div>
        <div className={`${tabStyles.tabContent} ${activeTab === 'points-config' ? tabStyles.active : ''}`}>
          {activeTab === 'points-config' && ActiveComponent && (
            <ActiveComponent
              groupDetails={groupDetails}
              onGroupUpdate={loadGroupDetails}
              hasPermission={hasPermission}
              isAdminContext={isAdminContext}
            />
          )}
        </div>
        <div className={`${tabStyles.tabContent} ${activeTab === 'task-management' ? tabStyles.active : ''}`}>
          {activeTab === 'task-management' && ActiveComponent && (
            <ActiveComponent
              groupDetails={groupDetails}
              onGroupUpdate={loadGroupDetails}
              hasPermission={hasPermission}
              isAdminContext={isAdminContext}
            />
          )}
        </div>
        <div className={`${tabStyles.tabContent} ${activeTab === 'user-management' ? tabStyles.active : ''}`}>
          {activeTab === 'user-management' && ActiveComponent && (
            <ActiveComponent
              groupDetails={groupDetails}
              onGroupUpdate={loadGroupDetails}
              hasPermission={hasPermission}
              isAdminContext={isAdminContext}
            />
          )}
        </div>
        <div className={`${tabStyles.tabContent} ${activeTab === 'leader-board' ? tabStyles.active : ''}`}>
          {activeTab === 'leader-board' && ActiveComponent && (
            <ActiveComponent
              groupDetails={groupDetails}
              onGroupUpdate={loadGroupDetails}
              hasPermission={hasPermission}
              isAdminContext={isAdminContext}
            />
          )}
        </div>
        <div className={`${tabStyles.tabContent} ${activeTab === 'settings' ? tabStyles.active : ''}`}>
          {activeTab === 'settings' && ActiveComponent && (
            <ActiveComponent
              groupDetails={groupDetails}
              onGroupUpdate={loadGroupDetails}
              hasPermission={hasPermission}
              isAdminContext={isAdminContext}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupDetails;
